<template>
  <div>
    <div
      class="flex flex-row justify-between items-center mb-4 bg-white shadow-sm sm:rounded-t-md py-6 px-4 sm:px-6 lg:px-8"
    >
      <div class="flex flex-row items-center gap-4 w-full sm:w-1/2">
        <div class="flex flex-col w-full sm:w-1/2">
          <DateRangePicker v-model="date" @update:model-value="updateDate" />
        </div>
        <MultiSelect
          name="categories"
          :label="t('filter_categories')"
          :options="categoryOptions"
          :modelValue="selectedCategories"
          @update:model-value="updateCategories"
        />
        <MultiSelect
          v-if="isInsurerAdmin"
          name="insurer-manager"
          :label="t('filter_sales_manager')"
          :options="salesManagerOptions"
          :modelValue="selectedSalesManagers"
          @update:model-value="updateSalesManagers"
        />
      </div>
      <div class="flex items-center gap-4">
        <Pills
          v-if="isInsurerAdmin && !organizationId && !storeId"
          :modelValue="activeTabLabel"
          :options="tabOptions"
          @update:model-value="switchTab"
        />
        <SecondaryButton v-if="showBackButton" @click="goBack">{{ t("back_button") }}</SecondaryButton>
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center">
      <Loading />
    </div>
    <div v-else>
      <CommonAnalyticsTable
        :stats="stats"
        :total="total"
        :currentMonth="currentMonth"
        :statsClickable="isTableClickable"
        @rowClicked="rowClicked"
      />
    </div>
  </div>
</template>

<script>
import {
  DateRangePicker,
  Loading,
  MultiSelect,
  Pills,
  SecondaryButton,
  currentMonthRange,
  inputDateFormat,
  toCamelCase,
} from "@estaly/ui";
import CommonAnalyticsTable from "@/components/analytics/retail/utils/CommonAnalyticsTable.vue";
import { FILTER_OPTIONS_PER_PAGE } from "@/constants";
import { useI18n } from "vue-i18n";

function formatDate(date) {
  if (date == null || isNaN(new Date(date).getTime())) return null;
  return inputDateFormat(new Date(date));
}

import { mapActions, mapGetters, mapState } from "vuex";

const TAB_ORGANIZATIONS = "organizations";
const TAB_STORES = "stores";
function toArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

export default {
  components: {
    DateRangePicker,
    CommonAnalyticsTable,
    Loading,
    MultiSelect,
    Pills,
    SecondaryButton,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  async mounted() {
    if (!this.signedIn) {
      this.$router.push("/signin");
      return;
    }

    this.loadQueryParams();
    if (!this.$route.query.start) {
      this.date = currentMonthRange();
      this.updateCurrentMonth(this.date[1]);
    }
    await this.loadFilterOptions();
    this.getAnalytics();
  },

  data: () => {
    return {
      isLoading: false,
      activeTab: TAB_STORES,
      organizationId: null,
      storeId: null,
      stats: null,
      total: null,
      date: currentMonthRange(),
      currentMonth: "",
      selectedCategories: [],
      selectedSalesManagers: [],
    };
  },

  computed: {
    ...mapState("auth", ["signedIn"]),
    ...mapGetters("auth", ["isInsurerAdmin"]),
    ...mapGetters("contractDistributions", ["categoryOptions"]),
    ...mapGetters("insurerManagers", ["salesManagerOptions"]),

    tabOptions() {
      return [this.t("tab_organizations"), this.t("tab_stores")];
    },

    activeTabLabel() {
      return this.isOrganizationsTab ? this.t("tab_organizations") : this.t("tab_stores");
    },

    isOrganizationsTab() {
      return this.activeTab === TAB_ORGANIZATIONS;
    },

    organizationsView() {
      return this.isOrganizationsTab && !this.organizationId && !this.storeId;
    },

    storesView() {
      if (this.isOrganizationsTab) {
        return this.organizationId != null && !this.storeId;
      }
      return !this.storeId;
    },

    sellersView() {
      return this.storeId != null;
    },

    isTableClickable() {
      return this.organizationsView || this.storesView;
    },

    showBackButton() {
      if (this.isOrganizationsTab) {
        return this.storesView || this.sellersView;
      }
      return this.sellersView;
    },
  },

  methods: {
    ...mapActions("analytics", {
      getAnalyticsOrganizations: "getOrganizations",
      getAnalyticsStores: "getStores",
      getAnalyticsSellers: "getSellers",
    }),
    ...mapActions("contractDistributions", { fetchContractDistributions: "fetchIndex" }),
    ...mapActions("insurerManagers", { fetchInsurerManagers: "fetchIndex" }),

    async loadFilterOptions() {
      const requests = [this.fetchContractDistributions({ perPage: FILTER_OPTIONS_PER_PAGE })];
      if (this.isInsurerAdmin) requests.push(this.fetchInsurerManagers({ perPage: FILTER_OPTIONS_PER_PAGE }));
      await Promise.all(requests);
    },

    async getAnalytics() {
      this.isLoading = true;

      try {
        const start = Array.isArray(this.date) && this.date[0] ? formatDate(this.date[0]) : null;
        const end = Array.isArray(this.date) && this.date[1] ? formatDate(this.date[1]) : null;

        const filters = {
          categories: this.selectedCategories,
          insurerManagerIds: this.selectedSalesManagers,
        };

        let response;
        if (this.sellersView) {
          response = await this.getAnalyticsSellers({
            storeId: this.storeId,
            startDate: start,
            endDate: end,
            ...filters,
          });
        } else if (this.storesView) {
          response = await this.getAnalyticsStores({
            organizationId: this.isOrganizationsTab ? this.organizationId : null,
            startDate: start,
            endDate: end,
            ...filters,
          });
        } else {
          response = await this.getAnalyticsOrganizations({ startDate: start, endDate: end, ...filters });
        }
        if (response?.success) {
          const formattedResponse = toCamelCase(response.data);
          this.stats = formattedResponse.stats;
          this.total = formattedResponse.total;
        }
      } finally {
        this.isLoading = false;
      }
    },

    updateQueryParams() {
      const query = {};
      if (this.date && this.date.length === 2) {
        query.start = formatDate(this.date[0]);
        query.end = formatDate(this.date[1]);
      }
      query.tab = this.activeTab;
      if (this.organizationId) query.organizationId = this.organizationId;
      if (this.storeId) query.storeId = this.storeId;
      if (this.selectedCategories.length) query.categories = this.selectedCategories;
      if (this.selectedSalesManagers.length) query.insurerManagerIds = this.selectedSalesManagers;
      this.$router.push({ query });
    },

    loadQueryParams() {
      // A sales manager has no access to the organizations view, so it is always pinned to the stores tab.
      this.activeTab = this.isInsurerAdmin ? this.$route.query.tab || TAB_ORGANIZATIONS : TAB_STORES;
      this.organizationId = this.$route.query.organizationId || null;
      this.storeId = this.$route.query.storeId || null;
      this.selectedCategories = toArray(this.$route.query.categories);
      this.selectedSalesManagers = this.isInsurerAdmin ? toArray(this.$route.query.insurerManagerIds).map(Number) : [];
      if (this.$route.query.start && this.$route.query.end) {
        this.date = [new Date(this.$route.query.start), new Date(this.$route.query.end)];
      } else {
        this.date = currentMonthRange();
      }
      if (this.date[0] && this.date[1]) this.updateCurrentMonth(this.date[1]);
    },

    switchTab(option) {
      const index = this.tabOptions.indexOf(option);
      this.activeTab = index === 0 ? TAB_ORGANIZATIONS : TAB_STORES;
      this.organizationId = null;
      this.storeId = null;
      this.stats = null;
      this.total = null;
      this.updateQueryParams();
    },

    goBack() {
      this.isLoading = true;
      if (this.sellersView) {
        this.storeId = null;
      } else if (this.storesView && this.isOrganizationsTab) {
        this.storeId = null;
        this.organizationId = null;
      }
      this.updateQueryParams();
    },

    rowClicked(row) {
      this.isLoading = true;
      if (this.organizationsView) {
        this.organizationId = row.id;
        this.storeId = null;
      } else if (this.storesView) {
        this.storeId = row.id;
      }
      this.updateQueryParams();
    },

    updateDate(date) {
      this.date = date;
      this.updateQueryParams();
    },

    updateCategories(categories) {
      this.selectedCategories = categories;
      this.updateQueryParams();
    },

    updateSalesManagers(salesManagerIds) {
      this.selectedSalesManagers = salesManagerIds;
      this.updateQueryParams();
    },

    updateCurrentMonth(endDate) {
      const monthNames = [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ];
      const month = monthNames[endDate.getMonth()];
      const year = endDate.getFullYear();
      this.currentMonth = `${month} ${year}`;
    },
  },

  watch: {
    "$route.query"() {
      this.loadQueryParams();
      this.getAnalytics();
    },
  },
};
</script>

<i18n src="./Retail.i18n.json"></i18n>
